// Утилита для генерации полей формы из JSON Schema
import React from 'react';
import TagsInput from '../components/TagsInput/TagsInput';
import TemplateSelector from '../components/TemplateSelector/TemplateSelector';

// UI тексты - константы компонента, не из модели
const UI_TEXTS = {
    selectPlaceholder: 'Выберите...',
    defaultSection: 'Основное',
    requiredFieldText: 'обязательно для заполнения',
    emptyValue: '—',
    booleanTexts: {
        true: 'Да',
        false: 'Нет'
    }
};

/**
 * Получает тип input элемента на основе схемы поля
 */
export const getInputType = (schema) => {
    // Приоритет x-ui-widget над автоопределением
    if (schema['x-ui-widget']) {
        const widget = schema['x-ui-widget'];
        if (widget === 'textarea') return 'textarea';
        if (widget === 'select') return 'select';
        if (widget === 'password') return 'password';
        if (widget === 'checkbox') return 'checkbox';
        if (widget === 'radio') return 'radio';
        if (widget === 'date') return 'date';
        if (widget === 'datetime') return 'datetime-local';
        if (widget === 'number') return 'number';
        if (widget === 'email') return 'email';
        if (widget === 'url') return 'url';
        if (widget === 'color') return 'color';
        if (widget === 'file') return 'file';
        if (widget === 'tags') return 'tags';
        if (widget === 'template-selector') return 'template-selector';
    }

    // Проверяем x-ui-formType для tags и template-selector
    if (schema['x-ui-formType'] === 'tags') return 'tags';
    if (schema['x-ui-formType'] === 'template-selector') return 'template-selector';

    // Проверяем x-ui-formType для textarea
    if (schema['x-ui-formType'] === 'textarea') return 'textarea';

    // Автоопределение по format и type
    if (schema.format === 'password') return 'password';
    if (schema.format === 'email') return 'email';
    if (schema.format === 'date-time') return 'datetime-local';
    if (schema.format === 'uri' || schema.format === 'url') return 'url';
    if (schema.type === 'boolean') return 'checkbox';
    if (schema.type === 'integer' || schema.type === 'number') return 'number';
    if (schema.enum) return 'select';
    return 'text';
};

/**
 * Генерирует элемент формы на основе схемы поля
 */
export const generateFormField = (fieldName, schema, value, onChange, errors = {}, disabled = false, isCreating = false, models = null, formData = null) => {
    const inputType = getInputType(schema);
    const isRequired = schema.required || false;
    const error = errors[fieldName];
    const label = schema['x-ui-formLabel'] || schema.description || fieldName;
    const placeholder = schema['x-ui-formPlaceholder'] || '';
    // Используем x-ui-formHintOnCreate из схемы для управления hint при создании
    const hint = (schema['x-ui-formHintOnCreate'] === false && isCreating) ? null : schema['x-ui-formHint'];
    
    // Для числовых полей: если значение 0, показываем пустую строку в input
    const isNumberField = schema.type === 'integer' || schema.type === 'number';
    const displayValue = isNumberField && value === 0 ? '' : (value ?? schema.default ?? '');
    
    // Обработчик фокуса - выделяет весь текст (только для input и textarea)
    const handleFocus = (e) => {
        // select() работает только для text input и textarea
        if (e.target.select && typeof e.target.select === 'function') {
            e.target.select();
        }
    };
    
    // Обработчик изменения для числовых полей
    const handleNumberChange = (e) => {
        const val = e.target.value;
        // Если поле пустое, передаём 0 (или null если схема позволяет)
        if (val === '') {
            onChange({ target: { value: schema.nullable ? null : 0 } });
        } else {
            onChange(e);
        }
    };
    
    // Базовые props для всех инпутов
    const baseProps = {
        name: fieldName,
        value: displayValue,
        onChange: isNumberField ? handleNumberChange : onChange,
        onFocus: handleFocus,
        disabled,
        className: `${inputType === 'checkbox' ? '' : 'input'} ${error ? 'input-error' : ''}`,
    };
    
    // Для select с enum
    if (schema.enum || schema['x-ui-enumSource']) {
        let enumOptions = [];
        let enumLabels = {};
        
        // Получаем опции из x-ui-enumSource (например, "routes.roles")
        if (schema['x-ui-enumSource'] && models) {
            const [modelName, path] = schema['x-ui-enumSource'].split('.');
            const sourceModel = models.schemas[modelName];
            
            if (sourceModel && sourceModel[path]) {
                // Преобразуем объект ролей в массив опций
                enumOptions = Object.keys(sourceModel[path]);
                // Получаем labels из объекта
                Object.entries(sourceModel[path]).forEach(([key, roleData]) => {
                    enumLabels[key] = roleData.label || key;
                });
            }
        } else if (schema.enum) {
            // Используем обычный enum
            enumOptions = schema.enum;
            enumLabels = schema['x-ui-enumLabels'] || {};
        }
        
        // Не показываем пустую опцию, если есть значение по умолчанию или поле обязательное
        const showEmptyOption = !schema.default && !isRequired;
        
        return (
            <div className="form-group" key={fieldName}>
                <label className={`label ${isRequired ? 'required' : ''}`}>
                    {label}:
                </label>
                <select {...baseProps} className="select">
                    {showEmptyOption && <option value="">{UI_TEXTS.selectPlaceholder}</option>}
                    {enumOptions.map(option => (
                        <option key={option} value={option}>
                            {enumLabels[option] || option}
                        </option>
                    ))}
                </select>
                {hint && <small className="form-hint">{hint}</small>}
                {error && <span className="error-text">{error}</span>}
            </div>
        );
    }
    
    // Для checkbox
    if (inputType === 'checkbox') {
        return (
            <div className="form-group" key={fieldName}>
                <label className="checkbox-label">
                    <input
                        {...baseProps}
                        type="checkbox"
                        checked={value === true}
                    />
                    <span>{label}</span>
                </label>
                {hint && <small className="form-hint">{hint}</small>}
                {error && <span className="error-text">{error}</span>}
            </div>
        );
    }

    // Для tags input
    if (inputType === 'tags') {
        return (
            <div className="form-group" key={fieldName}>
                <label className={`label ${isRequired ? 'required' : ''}`}>
                    {label}:
                </label>
                <TagsInput
                    value={value || []}
                    onChange={(newTags) => {
                        // Вызываем onChange напрямую с fieldName и новым значением
                        onChange({
                            target: {
                                name: fieldName,
                                value: newTags,
                                type: 'tags'
                            }
                        });
                    }}
                    placeholder={placeholder || 'Добавить тег...'}
                    disabled={disabled}
                />
                {hint && <small className="form-hint">{hint}</small>}
                {error && <span className="error-text">{error}</span>}
            </div>
        );
    }

    // Для template-selector
    if (inputType === 'template-selector') {
        const templateConfig = schema['x-template-config'] || {};
        const filterByField = templateConfig.filterBy || 'type';
        const templateType = formData ? formData[filterByField] : null;

        return (
            <div className="form-group" key={fieldName}>
                <label className={`label ${isRequired ? 'required' : ''}`}>
                    {label}:
                </label>
                <TemplateSelector
                    templateType={templateType}
                    onSelectTemplate={(template) => {
                        // Вызываем специальный onChange для загрузки данных
                        onChange({
                            target: {
                                name: fieldName,
                                value: template.id,
                                type: 'template-selector',
                                templateData: template,
                                templateConfig: templateConfig
                            }
                        });
                    }}
                />
                {hint && <small className="form-hint">{hint}</small>}
                {error && <span className="error-text">{error}</span>}
            </div>
        );
    }

    // Для textarea
    if (inputType === 'textarea') {
        const rows = schema['x-ui-formRows'] || 5; // По умолчанию 5 строк

        // Обработчик Tab для вставки табуляции
        const handleKeyDown = (e) => {
            if (e.key === 'Tab') {
                e.preventDefault(); // Предотвращаем переход фокуса

                const target = e.target;
                const start = target.selectionStart;
                const end = target.selectionEnd;
                const value = target.value;

                // Вставляем таб в позицию курсора
                const newValue = value.substring(0, start) + '\t' + value.substring(end);

                // Обновляем значение через onChange
                onChange({ target: { name: fieldName, value: newValue } });

                // Восстанавливаем позицию курсора после обновления React
                setTimeout(() => {
                    target.selectionStart = target.selectionEnd = start + 1;
                }, 0);
            }
        };

        return (
            <div className="form-group" key={fieldName}>
                <label className={`label ${isRequired ? 'required' : ''}`}>
                    {label}:
                </label>
                <textarea
                    {...baseProps}
                    rows={rows}
                    minLength={schema.minLength}
                    maxLength={schema.maxLength}
                    placeholder={placeholder}
                    onKeyDown={handleKeyDown}
                    className={`textarea ${error ? 'input-error' : ''}`}
                    style={{ resize: 'vertical', minHeight: `${rows * 1.5}em` }}
                />
                {hint && <small className="form-hint">{hint || 'Используйте Tab для создания отступов абзацев'}</small>}
                {error && <span className="error-text">{error}</span>}
            </div>
        );
    }

    // Для обычных input
    return (
        <div className="form-group" key={fieldName}>
            <label className={`label ${isRequired ? 'required' : ''}`}>
                {label}:
            </label>
            <input 
                {...baseProps}
                type={inputType}
                min={schema.minimum}
                max={schema.maximum}
                minLength={schema.minLength}
                maxLength={schema.maxLength}
                placeholder={placeholder}
            />
            {hint && <small className="form-hint">{hint}</small>}
            {error && <span className="error-text">{error}</span>}
        </div>
    );
};

/**
 * Генерирует простой список полей формы без группировки
 */
export const generateFormFields = (properties, formData, onChange, prefix = '') => {
    if (!properties) return null;
    
    return Object.entries(properties).map(([fieldName, fieldSchema]) => {
        const fullFieldName = prefix ? `${prefix}.${fieldName}` : fieldName;
        const value = formData[fieldName];
        
        return generateFormField(
            fieldName,
            fieldSchema,
            value,
            (e) => onChange(fieldName, e.target.type === 'checkbox' ? e.target.checked : e.target.value),
            {},
            false,
            false,
            null,
            formData
        );
    });
};

/**
 * Генерирует все поля формы из схемы модели с группировкой по секциям
 */
export const generateFormFromSchema = (schema, formData, onChange, errors = {}, options = {}) => {
    if (!schema || !schema.properties) return null;
    
    const { exclude = [], include = null, disabled = {}, isCreating = false } = options;
    
    // Определяем какие поля показывать
    const fieldsToShow = include || Object.keys(schema.properties);
    
    // Собираем поля с их метаданными
    const fieldsWithMeta = fieldsToShow
        .filter(field => !exclude.includes(field))
        .map(fieldName => {
            const fieldSchema = schema.properties[fieldName];
            // Проверяем обычный required
            let isRequired = schema.required?.includes(fieldName);
            // Проверяем условный required через x-ui-requiredWhen
            if (!isRequired && fieldSchema['x-ui-requiredWhen']) {
                if (fieldSchema['x-ui-requiredWhen'] === 'creating' && isCreating) {
                    isRequired = true;
                } else if (fieldSchema['x-ui-requiredWhen'] === 'editing' && !isCreating) {
                    isRequired = true;
                }
            }
            
            return {
                fieldName,
                fieldSchema: { ...fieldSchema, required: isRequired },
                section: fieldSchema['x-ui-formSection'] || UI_TEXTS.defaultSection,
                sectionOrder: fieldSchema['x-ui-formSectionOrder'] || 999,
                sectionIcon: fieldSchema['x-ui-formSectionIcon'] || '',
                fieldOrder: fieldSchema['x-ui-formOrder'] || 999,
                column: fieldSchema['x-ui-formColumn'] || 'full'
            };
        });
    
    // Группируем по секциям
    const sections = {};
    fieldsWithMeta.forEach(field => {
        if (!sections[field.section]) {
            sections[field.section] = {
                name: field.section,
                order: field.sectionOrder,
                icon: field.sectionIcon,
                fields: []
            };
        }
        sections[field.section].fields.push(field);
    });
    
    // Сортируем секции и поля внутри них
    const sortedSections = Object.values(sections)
        .sort((a, b) => a.order - b.order);
    
    sortedSections.forEach(section => {
        section.fields.sort((a, b) => a.fieldOrder - b.fieldOrder);
    });
    
    // Рендерим секции с полями
    return sortedSections.map((section, sectionIndex) => (
        <div key={section.name} className="form-section">
            {section.name !== UI_TEXTS.defaultSection && (
                <div className="form-section-header">
                    {section.icon && <span className="section-icon">{section.icon}</span>}
                    <span className="section-title">{section.name}</span>
                </div>
            )}
            <div className="form-section-content">
                {/* Группируем поля по строкам для колонок */}
                {(() => {
                    const rows = [];
                    let currentRow = [];
                    
                    section.fields.forEach((field, index) => {
                        const fieldElement = generateFormField(
                            field.fieldName,
                            field.fieldSchema,
                            formData[field.fieldName],
                            onChange,
                            errors,
                            disabled[field.fieldName] || false,
                            isCreating,
                            null,
                            formData
                        );
                        
                        if (field.column === 'full') {
                            // Если есть текущая строка - добавляем её
                            if (currentRow.length > 0) {
                                rows.push(
                                    <div key={`row-${rows.length}`} className="form-row">
                                        {currentRow}
                                    </div>
                                );
                                currentRow = [];
                            }
                            // Добавляем полноширинное поле
                            rows.push(fieldElement);
                        } else {
                            // Добавляем в текущую строку
                            currentRow.push(
                                <div key={field.fieldName} className={`form-col form-col-${field.column}`}>
                                    {fieldElement}
                                </div>
                            );
                            
                            // Если в строке 2 поля или это последнее поле - завершаем строку
                            if (currentRow.length === 2 || index === section.fields.length - 1) {
                                rows.push(
                                    <div key={`row-${rows.length}`} className="form-row">
                                        {currentRow}
                                    </div>
                                );
                                currentRow = [];
                            }
                        }
                    });
                    
                    return rows;
                })()}
            </div>
        </div>
    ));
};

/**
 * Получает отображаемые колонки для таблицы из схемы
 */
export const getTableColumns = (schema, options = {}) => {
    if (!schema || !schema.properties) return [];
    
    const { isAdmin = false } = options;
    
    // Фильтруем поля которые должны отображаться в таблице
    const tableFields = Object.keys(schema.properties)
        .filter(field => {
            const prop = schema.properties[field];
            // Проверяем ui:table флаг
            if (!prop['x-ui-table']) return false;
            // Проверяем adminOnly флаг
            if (prop['x-ui-adminOnly'] && !isAdmin) return false;
            return true;
        })
        .map(field => {
            const prop = schema.properties[field];
            return {
                field,
                label: prop['x-ui-tableLabel'] || prop.description || field,
                order: prop['x-ui-tableOrder'] || 999,
                type: prop.type,
                format: prop.format || prop['x-ui-tableFormat'],
                enum: prop.enum,
                badge: prop['x-ui-tableBadge'],
                combineWith: prop['x-ui-tableCombineWith'],
                adminOnly: prop['x-ui-adminOnly']
            };
        })
        // Сортируем по порядку
        .sort((a, b) => a.order - b.order);
    
    return tableFields;
};

/**
 * Форматирует значение для отображения в таблице
 */
export const formatTableValue = (value, column, roleLabels = {}) => {
    if (value === null || value === undefined) return UI_TEXTS.emptyValue;
    
    if (column.format === 'date-time' && value) {
        return new Date(value).toLocaleDateString();
    }
    
    if (column.type === 'boolean') {
        return value ? UI_TEXTS.booleanTexts.true : UI_TEXTS.booleanTexts.false;
    }
    
    // Используем roleLabels из модели routes если передан
    if (column.field === 'role' && roleLabels[value]) {
        return roleLabels[value];
    }
    
    return value;
};

/**
 * Получает поля для формы из схемы
 */
export const getFormFields = (schema, options = {}) => {
    if (!schema || !schema.properties) return [];
    
    const { isCreating = false } = options;
    
    // Фильтруем поля которые должны отображаться в форме
    const formFields = Object.keys(schema.properties)
        .filter(field => {
            const prop = schema.properties[field];
            // Проверяем ui:form флаг
            if (!prop['x-ui-form']) return false;
            // Проверяем ui:formOnlyWhen
            if (prop['x-ui-formOnlyWhen'] === 'creating' && !isCreating) return false;
            if (prop['x-ui-formOnlyWhen'] === 'editing' && isCreating) return false;
            return true;
        })
        .map(field => {
            const prop = schema.properties[field];
            return {
                field,
                label: prop['x-ui-formLabel'] || prop.description || field,
                order: prop['x-ui-formOrder'] || 999,
                adminOnly: prop['x-ui-adminOnly'],
                required: schema.required?.includes(field)
            };
        })
        // Сортируем по порядку
        .sort((a, b) => a.order - b.order)
        .map(item => item.field);
    
    return formFields;
};

/**
 * Проверяет обязательные поля
 */
export const validateRequired = (schema, formData) => {
    const errors = {};
    
    if (!schema.required) return errors;
    
    schema.required.forEach(field => {
        const value = formData[field];
        // Проверяем что значение действительно пустое (не 0, не false)
        if (value === undefined || value === null || value === '') {
            const fieldName = schema.properties[field]?.['x-ui-formLabel'] || 
                           schema.properties[field]?.description || 
                           field;
            errors[field] = `${fieldName} ${UI_TEXTS.requiredFieldText}`;
        }
    });
    
    return errors;
};